import {
	Component, Input, OnInit, OnChanges, SimpleChanges, OnDestroy,
 ChangeDetectorRef, AfterViewInit, ElementRef
} from '@angular/core';
import { Observable, Subject } from 'rxjs/Rx';
import { MessageService } from 'base';
import { DateService } from 'base';
import { MonitorService } from 'monitor';
import { IdentityService } from 'identity';
import { NavigationService, NetFacadeService } from 'netview';
import { InfoSetView, InfoSetViewEntry } from 'UvExplorerServer.Dtos.TS/Session';
import { NetViewDto } from 'UvExplorerServer.Dtos.TS/NetViews';

export class ConfigHistoryEntry {
	public groupKey = "";
	public deviceName = "";
	public configName = "";
	public timestamp = "";
	public configValue = "";
}

@Component({
	selector: 'config-history',
	templateUrl: './confighistory.component.html',
	styleUrls: ['./confighistory.component.scss']
})
export class ConfigHistoryComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {

	@Input()
	public visible: boolean;

	@Input()
	public monitorType: string;

	@Input()
	public deviceGuid: string;

	@Input()
	public monitorPolicyGuid: string;

	private data: InfoSetView;
	public entries: ConfigHistoryEntry[];
	public rowGroupMetadata: any;

	private destroyed: boolean;
	private eventsInterval: number;
	private timerId: any;

	public configKey: string;
	public configText: string;

	public displayViewConfigDialog: boolean;

	constructor(private element: ElementRef,
		private monitorService: MonitorService,
		private messageService: MessageService,
		private identityService: IdentityService,
		private navService: NavigationService,
		private netFacadeService: NetFacadeService,
		private dateService: DateService) {

		this.monitorType = "";
		this.deviceGuid = "";
		this.monitorPolicyGuid = "";

		this.data = null;
		this.entries = [];
		this.rowGroupMetadata = {};

		this.destroyed = false;
		this.eventsInterval = 0;
		this.timerId = undefined;

		this.displayViewConfigDialog = false;
		this.resetConfigProps();
	}

	public formatDateHm(date: string | Date): string {
		return this.dateService.formatDateHm(date);
	}

	public ngOnInit(): void {
		if (this.visible && this.navService.isNetworkLoaded) {
			this.startTimer();
		}
	}

	public ngAfterViewInit(): void {

	}

	public ngOnChanges(changes: SimpleChanges): void {

		if (this.destroyed)
			return;

		this.stopTimer();
		if (this.visible && this.navService.isNetworkLoaded) {
			this.retrieveMonitorHistory(true);
			this.startTimer();
		}
	}

	public ngOnDestroy(): void {
		this.stopTimer();
	}

	private setEventsInterval(): void {
		this.eventsInterval = 0;

		let netViewGuid = this.navService.location.netViewGuid;
		let network: NetViewDto = this.netFacadeService.findNetwork(netViewGuid);
		if (network) {
			this.eventsInterval = network.EventsInterval;
		}
		else {
			this.messageService.displayErrorMessage("Could not set events interval");
		}
	}

	private startTimer(): void {

		this.setEventsInterval();

		if (!this.timerId && this.visible && this.eventsInterval > 0) {
			let timeoutMillis = this.eventsInterval * 1000;
			this.timerId = setInterval(() => {
				this.retrieveMonitorHistory(false);
			}, timeoutMillis);
		}
	}

	private stopTimer(): void {
		if (this.timerId) {
			clearInterval(this.timerId);
			this.timerId = undefined;
		}
	}

	private retrieveMonitorHistory(showBusy: boolean): Observable<boolean> {

		if (!!this.monitorType && (!!this.deviceGuid || !!this.monitorPolicyGuid)) {

			let sessionGuid = this.identityService.sessionGuid;
			let netViewGuid = this.navService.location.netViewGuid;
			let agentGuid = this.navService.location.agentGuid;
			let schedDiscGuid = this.navService.location.schedDiscGuid;
			let schedDiscRsltGuid = this.navService.location.schedDiscRsltGuid;

			let subject = new Subject<any>();

			this.monitorService.getMonitorHistory(sessionGuid, netViewGuid, agentGuid, schedDiscGuid,
				schedDiscRsltGuid, this.monitorType, this.monitorPolicyGuid, this.deviceGuid, showBusy).subscribe(
					(result: InfoSetView) => {
						this.data = result;
						this.rebuildEntries();
						subject.next(true);
					},
					(error) => {
						this.data = null;
						this.rebuildEntries();
						this.messageService.displayErrorMessage("Could not retrieve monitor history");
						subject.error(false);
					});

			return subject;
		}
		else {
			this.data = null;
			this.rebuildEntries();
			return new Observable<boolean>(observer => {
				observer.error(false);
			});
		}
	}

	private rebuildEntries(): void {
		this.entries = [];

		if (!!this.data) {
			this.data.entries.forEach((isvEntry: InfoSetViewEntry) => {
				let entry = new ConfigHistoryEntry();
				entry.deviceName = isvEntry.values[0].value;
				entry.configName = isvEntry.values[1].value;
				entry.timestamp = isvEntry.values[2].value;
				entry.configValue = isvEntry.values[3].value;
				entry.groupKey = isvEntry.groupKey;

				this.entries.push(entry);
			});
		}

		this.rebuildRowGroupMetaData();
	}

	private rebuildRowGroupMetaData() {
		this.rowGroupMetadata = {};
		if (!!this.data) {
			for (let i = 0; i < this.entries.length; i++) {
				let rowData = this.entries[i];
				let groupKey = rowData.groupKey;
				if (i == 0) {
					this.rowGroupMetadata[groupKey] = { index: 0, size: 1 };
				}
				else {
					let previousRowData = this.entries[i - 1];
					let previousRowGroup = previousRowData.groupKey;
					if (groupKey === previousRowGroup) {
						console.assert(!!this.rowGroupMetadata[groupKey]);
						this.rowGroupMetadata[groupKey].size++;
					}
					else {
						console.assert(!this.rowGroupMetadata[groupKey]);
						this.rowGroupMetadata[groupKey] = { index: i, size: 1 };
					}
				}
			}
		}
	}

	private resetConfigProps(): void {
		this.configKey = "";
		this.configText = "";
	}

	private setConfigPropsFromEntry(entry): void {
		this.configKey = entry.configName;
		this.configText = entry.configValue;
	}

	public viewConfig(entry): void {
		this.setConfigPropsFromEntry(entry);
		this.displayViewConfigDialog = true;
	}

	public closeViewConfigDialog(): void {
		this.displayViewConfigDialog = false;
		this.resetConfigProps();
	}

	public exportConfig(): void {
		//let data = [this.config];
		//let blob = new Blob(data, { type: "application/vnd.ms-powerpoint" });
		//let blobUrl = window.URL.createObjectURL(blob);
		//window.open(blobUrl);
		this.download(this.configText, this.configKey + ".txt", "text/plain");
	}

	// Function to download data to a file
	private download(data, filename, type): void {
		var file = new Blob([data], { type: type });
		var a = document.createElement("a"),
			url = URL.createObjectURL(file);
		a.href = url;
		a.download = filename;
		document.body.appendChild(a);
		a.click();
		setTimeout(function () {
			document.body.removeChild(a);
			window.URL.revokeObjectURL(url);
		}, 0);
		
	}

}
